import React, { useMemo } from 'react';
import {
  AlertTriangle, Activity, CreditCard, Building2, Plus, X,
  StickyNote, Phone, Mail, Users, Search, FileCheck, MessageSquare
} from 'lucide-react';

const CasoResumen = ({ caso, onOpenSearch, onAddNote, onRemoveCuenta, onRemoveEmpresa, navigate }) => {
  // Consolidar notas del caso + notas de alertas
  const todasLasNotas = useMemo(() => {
    const notas = [];
    // Notas del timeline del caso
    (caso.timeline || []).filter(e => e.tipo === 'NOTA' || e.tipo === 'NOTA_ALERTA').forEach(n => {
      notas.push({
        fuente: n.tipo === 'NOTA_ALERTA' ? 'alerta' : 'caso',
        alerta_tipo: n.metadata?.alerta_tipo,
        tipo_nota: n.metadata?.tipo_nota || n.metadata?.accion,
        descripcion: n.descripcion,
        usuario: n.usuario,
        fecha: n.fecha,
      });
    });
    // Notas dentro de alertas embebidas
    (caso.alertas || []).forEach(a => {
      (a.notas || []).forEach(n => {
        // Evitar duplicados con timeline
        if (!notas.some(x => x.fecha === n.fecha && x.descripcion === n.comentario)) {
          notas.push({
            fuente: 'alerta',
            alerta_tipo: a.tipo || a.nombre_display,
            tipo_nota: n.tipo,
            descripcion: n.comentario,
            usuario: n.usuario,
            fecha: n.fecha,
          });
        }
      });
    });
    return notas.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)).slice(0, 5);
  }, [caso]);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-sm flex items-center gap-2 text-amber-800">
              <AlertTriangle size={16} /> Alertas
            </h3>
            <button onClick={() => onOpenSearch('alerta')} className="p-1 rounded hover:bg-amber-100 text-amber-600">
              <Plus size={16} />
            </button>
          </div>
          <p className="text-2xl font-bold text-amber-700">{caso.total_alertas || caso.alertas?.length || caso.alertas_ids?.length || 0}</p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-sm flex items-center gap-2 text-green-800">
              <Activity size={16} /> Movimientos
            </h3>
            <button onClick={() => onOpenSearch('movimiento')} className="p-1 rounded hover:bg-green-100 text-green-600">
              <Plus size={16} />
            </button>
          </div>
          <p className="text-2xl font-bold text-green-700">{caso.movimientos_ids?.length || 0}</p>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-sm flex items-center gap-2 text-blue-800">
              <CreditCard size={16} /> Cuentas
            </h3>
            <button onClick={() => onOpenSearch('cuenta')} className="p-1 rounded hover:bg-blue-100 text-blue-600">
              <Plus size={16} />
            </button>
          </div>
          <p className="text-2xl font-bold text-blue-700">{caso.cuentas_relacionadas?.length || 0}</p>
        </div>
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-sm flex items-center gap-2 text-purple-800">
              <Building2 size={16} /> Empresas
            </h3>
            <button onClick={() => onOpenSearch('empresa')} className="p-1 rounded hover:bg-purple-100 text-purple-600">
              <Plus size={16} />
            </button>
          </div>
          <p className="text-2xl font-bold text-purple-700">{caso.empresas_relacionadas?.length || 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium text-sm mb-2 flex items-center gap-2 text-slate-700">
            <CreditCard size={14} /> Cuentas Relacionadas
          </h4>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {caso.cuentas_relacionadas?.slice(0, 5).map((cuit, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs p-1.5 bg-white rounded">
                <span className="font-mono">{cuit}</span>
                <button onClick={() => onRemoveCuenta(cuit)} className="text-red-500 hover:text-red-700">
                  <X size={12} />
                </button>
              </div>
            ))}
            {!caso.cuentas_relacionadas?.length && (
              <p className="text-xs text-slate-400 text-center py-2">Sin cuentas vinculadas</p>
            )}
          </div>
        </div>
        <div className="p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium text-sm mb-2 flex items-center gap-2 text-slate-700">
            <Building2 size={14} /> Empresas Relacionadas
          </h4>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {caso.empresas_relacionadas?.slice(0, 5).map((emp, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs p-1.5 bg-white rounded">
                <span className="truncate max-w-[150px]">{emp.razon_social || emp.id}</span>
                <button onClick={() => onRemoveEmpresa(emp.id)} className="text-red-500 hover:text-red-700">
                  <X size={12} />
                </button>
              </div>
            ))}
            {!caso.empresas_relacionadas?.length && (
              <p className="text-xs text-slate-400 text-center py-2">Sin empresas vinculadas</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm flex items-center gap-2 text-pink-800">
              <StickyNote size={14} /> Últimas Notas
            </h4>
            <button onClick={onAddNote} className="p-1 rounded hover:bg-pink-100 text-pink-600">
              <Plus size={16} />
            </button>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {todasLasNotas.length > 0 ? todasLasNotas.map((nota, idx) => (
              <div key={idx} className="p-2 bg-white rounded text-xs">
                <div className="flex items-center gap-2 mb-1">
                  {nota.fuente === 'alerta' ? (
                    <MessageSquare size={12} className="text-sky-500" />
                  ) : nota.tipo_nota === 'LLAMADA' ? (
                    <Phone size={12} className="text-pink-500" />
                  ) : nota.tipo_nota === 'EMAIL' ? (
                    <Mail size={12} className="text-pink-500" />
                  ) : (
                    <StickyNote size={12} className="text-pink-500" />
                  )}
                  <span className="font-medium text-slate-700">
                    {nota.fuente === 'alerta' ? `Alerta: ${nota.alerta_tipo || 'N/A'}` : (nota.tipo_nota || 'Nota')}
                  </span>
                  <span className="text-slate-400 ml-auto text-[10px]">{nota.usuario} - {new Date(nota.fecha).toLocaleDateString()}</span>
                </div>
                <p className="text-slate-600 line-clamp-2">{nota.descripcion}</p>
              </div>
            )) : (
              <p className="text-xs text-slate-400 text-center py-3">Sin notas registradas</p>
            )}
          </div>
        </div>

        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm flex items-center gap-2 text-indigo-800">
              <FileCheck size={14} /> Solicitudes Vinculadas
            </h4>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {caso.solicitudes_vinculadas?.slice(0, 3).map((sol, idx) => (
              <div key={idx} className="p-2 bg-white rounded text-xs flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-700">{sol.referencia}</p>
                  <p className="text-slate-500">{sol.tipo} - {sol.estado}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  sol.estado === 'APROBADA' ? 'bg-green-100 text-green-700' :
                  sol.estado === 'RECHAZADA' ? 'bg-red-100 text-red-700' :
                  sol.estado === 'PENDIENTE' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {sol.estado}
                </span>
              </div>
            ))}
            {!caso.solicitudes_vinculadas?.length && (
              <p className="text-xs text-slate-400 text-center py-3">Sin solicitudes vinculadas</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasoResumen;
